import axios from 'axios';
import config from 'shared/constants/config';
import { getAccessTokenFromLocalStorage } from 'utils/local-storage';
import store from 'store';
import { authActions } from 'features/auth/redux/auth.actions';
import { CustomAxiosRequestConfig } from 'api/types';
import { addToQueue } from 'features/auth/utils/refresh-queue';

// axios 1.18.x tripped CRA's warnings-as-errors on
// `import/no-named-as-default-member`, but the named `create` is only a
// type-level export (runtime import fails), so `axios.create` is the
// canonical usage and the rule is a false positive here.
// eslint-disable-next-line import/no-named-as-default-member
const api = axios.create({
  baseURL: `${config.API_URL}/v1`,
});

api.interceptors.request.use(
  (config) => {
    const token = getAccessTokenFromLocalStorage();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest: CustomAxiosRequestConfig = error.config;

    if (!originalRequest || !originalRequest.headers) {
      return Promise.reject(error);
    }

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest._skipAuthRefresh
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          addToQueue({
            resolve: (token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(api(originalRequest));
            },
            reject: (err) => {
              reject(err);
            },
          });
        });
      }

      isRefreshing = true;
      store.dispatch(authActions.refreshTokenRequest());

      return new Promise((resolve, reject) => {
        addToQueue({
          resolve: (token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
            isRefreshing = false;
          },
          reject: (err) => {
            reject(err);
            isRefreshing = false;
          },
        });
      });
    }

    return Promise.reject(error);
  }
);

export default api;
